// The record of the creator's first conversation, filled in as Mor learns: what brings them
// here, who is in their life, what is already arranged, and where they could begin. The
// conversation speaks; this page records. Each section appears once it has something to
// show, so before anything is learned the page is the title and the lede. The only controls
// are the links to a person or a suggestion and the small "Add a file" link, which sits in
// the existing arrangements once there is text or a file there.

import { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import type { ApiClient } from '../api/client.js';
import type { FileView, Suggestion } from '../api/contract.js';
import { relationLabel, topicPhrase } from '../format.js';
import { areaFor } from '../plan/areas.js';
import { useOpenSuggestion } from '../plan/openSuggestion.js';
import { viewerIsPrincipal } from '../plan/snapshot.js';
import type { PlanState } from '../plan/usePlan.js';
import { AddFile } from './AddFile.js';
import { FileLine } from './FileRows.js';

export type DiscoveryPageProps = {
  state: PlanState;
  client: ApiClient;
  planId: string;
  /** The reader's own plan-level conversation, which an added file records as its origin. */
  originThreadId: string | null;
};

export function DiscoveryPage({
  state,
  client,
  planId,
  originThreadId,
}: DiscoveryPageProps): JSX.Element | null {
  const snapshot = state.snapshot;
  const navigate = useNavigate();
  const { open, opening, problem } = useOpenSuggestion({
    client,
    planId,
    state,
    navigate,
  });
  const [files, setFiles] = useState<FileView[]>([]);
  const [filesVersion, setFilesVersion] = useState(0);
  const reloadFiles = useCallback(() => setFilesVersion((value) => value + 1), []);
  useEffect(() => {
    let current = true;
    client.listFiles(planId).then(
      (loaded) => {
        if (current) {
          setFiles(loaded);
        }
      },
      () => undefined,
    );
    return () => {
      current = false;
    };
  }, [client, planId, snapshot, filesVersion]);
  if (snapshot === null) {
    return null;
  }
  const objective =
    snapshot.objectives.find((item) => item.topic === null && item.text !== null)?.text ??
    null;
  const others = snapshot.people.filter((person) => !person.self);
  const arrangements = snapshot.plan.firstConversation?.arrangements ?? null;
  const suggestions = [...(snapshot.plan.firstConversation?.suggestions ?? [])].sort(
    (a, b) => a.rank - b.rank,
  );

  const suggestionRow = (suggestion: Suggestion): JSX.Element => {
    const topic = snapshot.topics.find((candidate) => candidate.key === suggestion.topic);
    const area = areaFor(suggestion.topic);
    const title =
      suggestion.title ?? (topic === undefined ? suggestion.topic : topicPhrase(topic));
    const openUrl =
      suggestion.issue !== null
        ? `/plans/${planId}/topics/${suggestion.topic}/issues/${suggestion.issue}`
        : suggestion.opened && suggestion.kind === 'topic'
          ? `/plans/${planId}/topics/${suggestion.topic}/issues`
          : null;
    return (
      <li key={suggestion.id} className="person issueRow">
        <span className="person__identity">
          {area === null ? null : <span className="suggestionRow__area">{area}</span>}
          <span className="person__name">{title}</span>
          <span className="person__detail">{suggestion.reason}</span>
          {openUrl !== null ? (
            <Link className="suggestionRow__link" to={openUrl}>
              Open →
            </Link>
          ) : (
            <button
              type="button"
              className="discoveryLink suggestionRow__link"
              disabled={opening !== null}
              onClick={() => void open(suggestion)}
            >
              {suggestion.kind === 'issue'
                ? 'Explore this issue →'
                : 'Start a broader review →'}
            </button>
          )}
        </span>
      </li>
    );
  };

  return (
    <section className="page issue">
      <h2 className="page__title">Getting to know you</h2>
      <p className="page__subtitle">
        Start with what prompted you to think about planning. Mor will build a picture of
        what matters and help you find a useful place to begin.
      </p>
      {objective === null ? null : (
        <section className="issue__section">
          <h3 className="sectionLabel">What brings you here</h3>
          <p>{objective}</p>
        </section>
      )}
      {others.length === 0 ? null : (
        <section className="issue__section">
          <h3 className="sectionLabel">People identified so far</h3>
          <ul className="people__list people__list--lines">
            {others.map((person) => (
              <li key={person.id} className="personLine">
                <span className="personLine__text">
                  <Link
                    className="personLine__name"
                    to={`/plans/${planId}/people/${person.id}`}
                  >
                    {person.name}
                  </Link>
                  {person.relation === null ? null : (
                    <span className="personLine__detail">
                      {' · '}
                      {relationLabel(person.relation)}
                    </span>
                  )}
                </span>
              </li>
            ))}
          </ul>
        </section>
      )}
      {arrangements === null && files.length === 0 ? null : (
        <section className="issue__section">
          <h3 className="sectionLabel">Existing arrangements</h3>
          {arrangements === null ? null : <p>{arrangements}</p>}
          {files.length === 0 ? null : (
            <ul className="fileLines">
              {files.map((file) => (
                <FileLine
                  key={file.id}
                  client={client}
                  planId={planId}
                  file={file}
                  people={snapshot.people}
                  viewerIsPrincipal={viewerIsPrincipal(snapshot)}
                  onChanged={reloadFiles}
                />
              ))}
            </ul>
          )}
          <AddFile
            client={client}
            planId={planId}
            people={snapshot.people}
            viewerIsPrincipal={viewerIsPrincipal(snapshot)}
            originThreadId={originThreadId}
            asLink
            onAdded={reloadFiles}
          />
        </section>
      )}
      {suggestions.length === 0 ? null : (
        <section className="issue__section">
          <h3 className="sectionLabel">Where you could begin</h3>
          <p className="issue__note">Suggestions, not decisions</p>
          <ul className="people__list">{suggestions.map(suggestionRow)}</ul>
        </section>
      )}
      {problem === null ? null : (
        <p className="page__problem" role="alert">
          {problem}
        </p>
      )}
    </section>
  );
}
